import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { User } from '@zerops/models/user';
import { UserTokenCreateResponse } from '@zerops/zerops/core/user-token-base';
import { HandoverReader } from './z3-handover.check';

const PAGE = 100;

const readAll = async <T>(page: (offset: number) => Promise<{ items: T[]; total: number; }>): Promise<T[]> => {
  const all: T[] = [];
  for (;;) {
    const { items, total } = await page(all.length);
    all.push(...items);
    if (!items.length || all.length >= total) { return all; }
  }
};

@Injectable({ providedIn: 'root' })
export class Z3HandoverApi implements HandoverReader {
  #httpClient = inject(HttpClient);

  /**
   * Mints a personal access token for a client. The secret is in this response
   * and nowhere else — the list endpoint returns names and ids only — so it is
   * handed straight to the redirect and never stored here.
   */
  create$(name: string): Observable<UserTokenCreateResponse> {
    return this.#httpClient.post<UserTokenCreateResponse>('/api/user-token', { name });
  }

  delete$(id: string) {
    return this.#httpClient.delete(`/api/user-token/${id}`);
  }

  // The destination check's reads, with this app's session.

  async memberships() {
    return (await firstValueFrom(this.#httpClient.get<User>('/api/user/info'))).clientUserList ?? [];
  }

  projects(clientId: string) {
    return readAll(async (offset) => {
      const { items, totalHits } = await firstValueFrom(this.#httpClient.post<{ items: Array<{ id: string; name: string; }>; totalHits: number; }>(
        '/api/project/search',
        { search: [ { name: 'clientId', operator: 'eq', value: clientId } ], limit: PAGE, offset }
      ));
      return { items: items ?? [], total: totalHits };
    });
  }

  async routedDomains(projectId: string) {
    const routings = await readAll(async (offset) => {
      const { list, total } = await firstValueFrom(this.#httpClient.get<{
        list: Array<{ domains: Array<{ domainName: string; sslStatus: string; }>; }>; total: number;
      }>(
        `/api/project/${projectId}/public-http-routing`,
        { params: { limit: PAGE, offset } }
      ));
      return { items: list ?? [], total };
    });
    return routings.flatMap(({ domains }) => domains);
  }
}
